import DenyFromWaitlist from '../../../../components/AffordableWaitlistActions/DenyFromWaitlist';
import CancelFromWaitlist from '../../../../components/AffordableWaitlistActions/CancelFromWaitlist';
import ReinstateFromWaitlist from '../../../../components/AffordableWaitlistActions/ReinstateFromWaitlist';
import AddToWaitlist from '../../../../components/AffordableWaitlistActions/AddToWaitlist';

/**
 * Thin rendering wrapper: given a pending action name and an affordable
 * waitlist id, renders the matching action modal. It intentionally does NOT
 * know about callers, business rules, or per-action payload differences.
 *
 * IMPORTANT — architectural guardrail (do not violate):
 * This wrapper must stay a dumb "render the modal matching pendingAction"
 * switch forever. Never add conditional/business logic branches here, and
 * never add a new action whose payload doesn't fit the shared contract
 * (organizationId + propertyId + affordableWaitlistId in, onClose/onSuccess
 * out, where onClose means "the user dismissed this modal" and onSuccess
 * means "this action completed" — carrying a
 * `{ action, affordableWaitlistId, ... }` descriptor, and leaving it to the
 * caller to close). If a new action needs
 * extra/different data or caller-specific behavior (e.g. SelectFromWaitlist,
 * which needs a row-specific payload), it does NOT belong in this wrapper —
 * keep it local to the caller instead. This is how the legacy V1
 * ManageAffordableWaitlistModals dispatcher became a hard-to-maintain
 * monolith: one "just this one exception" branch at a time.
 *
 * ENG-29631 widened the input contract to also carry applicationId and
 * floorPlans. Reinstate is not a new action — it is an existing one that
 * stopped being a bare confirm: it now opens the shared wizard, which
 * prefills from the application and needs both. Deny and Cancel still take
 * affordableWaitlistId alone. The switch itself stays dumb — no branch was
 * added here — so the rest of the guardrail holds as written.
 *
 * ENG-31969 added Add. Like Reinstate it opens the shared wizard from applicationId and
 * floorPlans, and it takes no affordableWaitlistId because no waitlist record exists until it
 * succeeds. It fits the contract as written — no branch was added here, and the guardrail
 * above stands.
 */
const WaitlistV2StatusActionModals = ({
  organizationId,
  propertyId,
  affordableWaitlistId,
  applicationId,
  floorPlans,
  pendingAction,
  onClose,
  onSuccess,
}) => (
  <>
    <DenyFromWaitlist
      organizationId={organizationId}
      propertyId={propertyId}
      affordableWaitlistId={affordableWaitlistId}
      open={pendingAction === 'deny'}
      handleClose={onClose}
      onSuccess={onSuccess}
    />
    <CancelFromWaitlist
      organizationId={organizationId}
      propertyId={propertyId}
      affordableWaitlistId={affordableWaitlistId}
      open={pendingAction === 'cancel'}
      handleClose={onClose}
      onSuccess={onSuccess}
    />
    <ReinstateFromWaitlist
      organizationId={organizationId}
      propertyId={propertyId}
      affordableWaitlistId={affordableWaitlistId}
      applicationId={applicationId}
      floorPlans={floorPlans}
      open={pendingAction === 'reinstate'}
      handleClose={onClose}
      onSuccess={onSuccess}
    />
    <AddToWaitlist
      organizationId={organizationId}
      propertyId={propertyId}
      applicationId={applicationId}
      floorPlans={floorPlans}
      open={pendingAction === 'add'}
      handleClose={onClose}
      onSuccess={onSuccess}
    />
  </>
);

export default WaitlistV2StatusActionModals;
